// 多語系（簡中／繁中／英文／日文）
//
// 刻意不用 i18next 之類的套件：字串不多，院內環境也不希望多一個相依。
//
// 語言的決定順序：
//   1. 使用者自己在畫面上選過的（存在 localStorage，只影響這台裝置）
//   2. 院方在 /brand/brand.json 設定的 defaultLang（每個院區可以不同）
//   3. 預設：簡體中文
//
// 用法：
//   const t = useT();          t("menu.start")         → "开始"
//   t("meeting.kick", { name: "王醫師" })               → "踢掉 王醫師"
//   const [lang, setLang] = useLang();                  → 切換語言

import { useEffect, useState } from "react";

export const LANGS = [
  { code: "zh-CN", label: "简体中文" },
  { code: "zh-TW", label: "繁體中文" },
  { code: "en", label: "English" },
  { code: "ja", label: "日本語" },
];

export const DEFAULT_LANG = "zh-CN";

const DICT = {
  "zh-CN": {
    "app.defaultName": "手术指导",
    "menu.companyPlaceholder": "输入公司代号",
    "menu.roomPlaceholder": "输入房间名称",
    "menu.roleHost": "主持人（医师端）",
    "menu.roleGuest": "参与者（观看端）",
    "menu.start": "开始",
    "menu.add": "新增",
    "menu.language": "语言",
    "room.namePlaceholder": "房间名称",
    "room.cancel": "取消",
    "room.save": "保存",
    "room.add": "新增房间",
    "room.editTitle": "修改房间名称",
    "room.addTitle": "新增房间",
    "room.delete": "删除",
    "room.edit": "修改",
    "room.nameRequired": "请输入房间名称",
    "meeting.loading": "会议载入中…",
    "meeting.host": "主持人",
    "meeting.guest": "参与者",
    "meeting.muteAll": "静音所有人",
    "meeting.selectParticipant": "选择参与者",
    "meeting.kick": "踢掉 {name}",
    "meeting.removeParticipant": "移除参与者",
    "meeting.noParticipants": "目前没有其他参与者",
    "meeting.kickConfirm": "确定要将「{name}」移出会议？",
    "meeting.whiteboard": "白板",
    "meeting.tokenFailed": "取得会议凭证失败：{error}",
    "meeting.tokenServerDown": "无法连线到 Token 服务器",
    "rotate.title": "请将手机转为<b>横向</b>使用",
    "rotate.hint": "直向时画面会缩得很小，不容易看清楚与标注",
    "rotate.continue": "仍要直向使用",
    "license.checking": "正在检查授权",
    "license.notLicensed": "ORMasterMeeting 尚未授权",
    "license.wait": "请稍候。",
    "license.howto": "请在服务器执行「安装精灵.bat」，产生 Token 并导入 License.dat。",
    "license.retry": "重新检查",
    "license.unreachable": "无法连线到授权服务，请确认 ORMasterMeeting 服务是否已启动。",
  },
  "zh-TW": {
    "app.defaultName": "手術指導",
    "menu.companyPlaceholder": "輸入公司代號",
    "menu.roomPlaceholder": "輸入房間名稱",
    "menu.roleHost": "主持人（醫師端）",
    "menu.roleGuest": "參與者（觀看端）",
    "menu.start": "開始",
    "menu.add": "新增",
    "menu.language": "語言",
    "room.namePlaceholder": "房間名稱",
    "room.cancel": "取消",
    "room.save": "儲存",
    "room.add": "新增房間",
    "room.editTitle": "修改房間名稱",
    "room.addTitle": "新增房間",
    "room.delete": "刪除",
    "room.edit": "修改",
    "room.nameRequired": "請輸入房間名稱",
    "meeting.loading": "會議載入中…",
    "meeting.host": "主持人",
    "meeting.guest": "參與者",
    "meeting.muteAll": "靜音所有人",
    "meeting.selectParticipant": "選擇參與者",
    "meeting.kick": "踢掉 {name}",
    "meeting.removeParticipant": "移除參與者",
    "meeting.noParticipants": "目前沒有其他參與者",
    "meeting.kickConfirm": "確定要將「{name}」移出會議？",
    "meeting.whiteboard": "白板",
    "meeting.tokenFailed": "取得會議憑證失敗：{error}",
    "meeting.tokenServerDown": "無法連線到 Token 伺服器",
    "rotate.title": "請將手機轉為<b>橫向</b>使用",
    "rotate.hint": "直向時畫面會縮得很小，不容易看清楚與標註",
    "rotate.continue": "仍要直向使用",
    "license.checking": "正在檢查授權",
    "license.notLicensed": "ORMasterMeeting 尚未授權",
    "license.wait": "請稍候。",
    "license.howto": "請在伺服器執行「安裝精靈.bat」，產生 Token 並匯入 License.dat。",
    "license.retry": "重新檢查",
    "license.unreachable": "無法連線到授權服務，請確認 ORMasterMeeting 服務是否已啟動。",
  },
  en: {
    "app.defaultName": "Surgical Guidance",
    "menu.companyPlaceholder": "Company code",
    "menu.roomPlaceholder": "Room name",
    "menu.roleHost": "Host (surgeon)",
    "menu.roleGuest": "Participant (viewer)",
    "menu.start": "Start",
    "menu.add": "Add",
    "menu.language": "Language",
    "room.namePlaceholder": "Room name",
    "room.cancel": "Cancel",
    "room.save": "Save",
    "room.add": "Add room",
    "room.editTitle": "Rename room",
    "room.addTitle": "Add room",
    "room.delete": "Delete",
    "room.edit": "Edit",
    "room.nameRequired": "Please enter a room name",
    "meeting.loading": "Loading meeting…",
    "meeting.host": "Host",
    "meeting.guest": "Participant",
    "meeting.muteAll": "Mute everyone",
    "meeting.selectParticipant": "Select participant",
    "meeting.kick": "Remove {name}",
    "meeting.removeParticipant": "Remove participant",
    "meeting.noParticipants": "No other participants",
    "meeting.kickConfirm": "Remove “{name}” from the meeting?",
    "meeting.whiteboard": "Whiteboard",
    "meeting.tokenFailed": "Could not get meeting credentials: {error}",
    "meeting.tokenServerDown": "Cannot reach the token server",
    "rotate.title": "Please rotate your phone to <b>landscape</b>",
    "rotate.hint": "In portrait the video is very small and hard to annotate",
    "rotate.continue": "Continue in portrait",
    "license.checking": "Checking license",
    "license.notLicensed": "ORMasterMeeting is not licensed",
    "license.wait": "Please wait.",
    "license.howto": "On the server, run the setup wizard to generate a token, then import License.dat.",
    "license.retry": "Check again",
    "license.unreachable": "Cannot reach the license service. Please make sure ORMasterMeeting is running.",
  },
  ja: {
    "app.defaultName": "手術指導",
    "menu.companyPlaceholder": "会社コードを入力",
    "menu.roomPlaceholder": "ルーム名を入力",
    "menu.roleHost": "ホスト（医師）",
    "menu.roleGuest": "参加者（視聴）",
    "menu.start": "開始",
    "menu.add": "追加",
    "menu.language": "言語",
    "room.namePlaceholder": "ルーム名",
    "room.cancel": "キャンセル",
    "room.save": "保存",
    "room.add": "ルームを追加",
    "room.editTitle": "ルーム名を変更",
    "room.addTitle": "ルームを追加",
    "room.delete": "削除",
    "room.edit": "編集",
    "room.nameRequired": "ルーム名を入力してください",
    "meeting.loading": "会議を読み込み中…",
    "meeting.host": "ホスト",
    "meeting.guest": "参加者",
    "meeting.muteAll": "全員をミュート",
    "meeting.selectParticipant": "参加者を選択",
    "meeting.kick": "{name} を退出させる",
    "meeting.removeParticipant": "参加者を退出させる",
    "meeting.noParticipants": "ほかの参加者はいません",
    "meeting.kickConfirm": "「{name}」を会議から退出させますか？",
    "meeting.whiteboard": "ホワイトボード",
    "meeting.tokenFailed": "会議の認証情報を取得できませんでした：{error}",
    "meeting.tokenServerDown": "トークンサーバーに接続できません",
    "rotate.title": "スマートフォンを<b>横向き</b>にしてください",
    "rotate.hint": "縦向きでは映像が小さく、見づらく書き込みにくくなります",
    "rotate.continue": "縦向きのまま使う",
    "license.checking": "ライセンスを確認中",
    "license.notLicensed": "ORMasterMeeting はライセンスされていません",
    "license.wait": "しばらくお待ちください。",
    "license.howto": "サーバーでセットアップウィザード（安装精灵.bat）を実行してトークンを作成し、License.dat を読み込んでください。",
    "license.retry": "再確認",
    "license.unreachable": "ライセンスサービスに接続できません。ORMasterMeeting が起動しているか確認してください。",
  },
};

const STORAGE_KEY = "ormaster.lang";
const htmlLang = (code) => ({ en: "en", ja: "ja", "zh-TW": "zh-Hant" }[code] || "zh-Hans");
const isSupported = (code) => LANGS.some((l) => l.code === code);

// localStorage 在隱私模式或被封鎖時會丟例外，讀寫都要包起來
const readStored = () => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isSupported(v) ? v : null;
  } catch {
    return null;
  }
};

let current = readStored() || DEFAULT_LANG;
const listeners = new Set();

const setCurrent = (code) => {
  if (!isSupported(code) || code === current) return;
  current = code;
  document.documentElement.lang = htmlLang(code);
  listeners.forEach((fn) => fn(code));
};

/** 使用者在畫面上選語言：記住在這台裝置 */
export const setLang = (code) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch {}
  setCurrent(code);
};

/** 院方預設語言（brand.json 的 defaultLang）：使用者自己選過就不蓋掉 */
export const applyDefaultLang = (code) => {
  if (!readStored() && isSupported(code)) setCurrent(code);
};

export const getLang = () => current;

export const translate = (lang, key, vars) => {
  let s = (DICT[lang] && DICT[lang][key]) || DICT[DEFAULT_LANG][key] || key;
  if (vars) s = s.replace(/\{(\w+)\}/g, (_, k) => (vars[k] == null ? "" : String(vars[k])));
  return s;
};

export const useLang = () => {
  const [lang, setLangState] = useState(current);
  useEffect(() => {
    listeners.add(setLangState);
    setLangState(current); // 訂閱前如果已經改過，補同步一次
    return () => listeners.delete(setLangState);
  }, []);
  return [lang, setLang];
};

export const useT = () => {
  const [lang] = useLang();
  return (key, vars) => translate(lang, key, vars);
};

// Jitsi 自己的介面語言代碼跟我們的不一樣
export const jitsiLang = (lang) => ({ "zh-CN": "zhCN", "zh-TW": "zhTW", en: "en", ja: "ja" }[lang] || "zhCN");

// 反過來：使用者在 Jitsi 設定（齒輪 → 一般）換語言時，我們的按鈕跟著換。
// 我們只支援四種，Jitsi 其他語言一律對應到英文。
export const fromJitsiLang = (code) => ({ zhCN: "zh-CN", zhTW: "zh-TW", ja: "ja" }[code] || "en");

// 初始就設好 <html lang>，讓瀏覽器選字型時用對的字形
document.documentElement.lang = htmlLang(current);
